import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import Image from 'next/image';
import React, { FC, useMemo } from 'react';
import { IoChevronForward } from 'react-icons/io5';

import { formatNumber } from '@app/helpers/format';
import { PoolTokensCell } from '@app/screens/pools/components/PoolTokensCell';
import { BadgesConfig } from '@app/types/pool';
import { Campaign } from '@app/hooks/merkl/useMerklCampaigns';
import { Vault } from '@app/hooks/plugins/useVaults';
import { getStrategyAcronim } from '@app/helpers/vaults';
import { useVaultsActivePools } from '@app/hooks/plugins/useVaultsActivePools';
import { useNftPoolAddress } from '@app/hooks/pools/useNftPoolAddress';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import { useIsNitroPool } from '@app/hooks/nitro/useIsNitroPool';
import { useNitroPoolDetails } from '@app/hooks/nitro/useNitroPoolDetails';
import { MetromCampaign } from '@app/hooks/metrom/useMetromCampaigns';
import { useMergedMarketMakerRewards } from '@app/hooks/metrom/useMergedMarketMakerRewards';
import { MarketMakerCampaignWidget } from '@app/screens/pools/components/MarketMakerCampaignWidget';
import { useAprWithTooltip } from '@app/hooks/metrom/useAprWithTooltip';
import { AprDetailsTooltip } from '@app/components/AprDetailsTooltip';
import { NitroCellRenderer } from '@app/screens/pools/components/NitroCellRenderer';
import { useConfig } from '@app/config';
import { useAlgebraPoolNitroDetails } from '@app/hooks/nitro/useAlgebraPoolNitroDetails';
import { useMarketMakerRewards } from '@app/hooks/merkl/useMarketMakerRewards';
import { LabeledValue } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep/components/LabeledValue';
import { formatUSD } from '@app/helpers/formatUSD';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { StrategyProvider } from '@app/state/mintStore';

interface Props {
  item: Vault;
  badges?: BadgesConfig;
  marketMakerCampaign?: {
    merkl: Array<Campaign & { amountUSD: number }>;
    metrom: Array<MetromCampaign & { amountUSD: number }>;
  };
  isSelectable?: boolean;
}

export const VaultDetails: FC<Props> = ({
  item,
  badges,
  marketMakerCampaign,
  isSelectable = true
}) => {
  const {
    strategy,
    actions: { setMode, setStrategy, setProvider }
  } = useLiquidityWizardState();
  const config = useConfig();
  const { token0, token1, tvl } = item;
  const { data: activePools } = useVaultsActivePools();
  const { data: nftPoolAddress } = useNftPoolAddress(item.lpToken.id);
  const { isNitroPool } = useIsNitroPool(nftPoolAddress);
  const { totalApr: nitroPoolApr } = useNitroPoolDetails(nftPoolAddress);
  const marketMaker = useMergedMarketMakerRewards(marketMakerCampaign, item.id);
  const isActivePool = useMemo(() => {
    if (!activePools || !nftPoolAddress) {
      return false;
    }

    return activePools[nftPoolAddress.toLowerCase()];
  }, [activePools, nftPoolAddress]);
  const nitroDetails = useAlgebraPoolNitroDetails(nftPoolAddress);

  const marketMakerAPR =
    marketMakerCampaign?.merkl
      .filter(c =>
        item.id
          ? c.campaignParameters.whitelist
              .map(w => w.toLowerCase())
              .includes(item.id.toLowerCase())
          : true
      )
      .reduce((acc: number, curr: Campaign) => {
        return acc + curr.apr;
      }, 0) ?? 0;

  const { totalNitroRewardsUSD, nitroRewards } = useMarketMakerRewards(
    marketMakerCampaign?.merkl,
    nitroDetails?.id
  );

  const { totalApr, farmBaseApr } = useFarmBaseApr(
    nftPoolAddress,
    item.tvl,
    item.apr,
    0
  );

  const aprValues = useAprWithTooltip({
    totalApr,
    farmBaseApr,
    swapFeesApr: item.apr,
    marketMakerAPR,
    nitroPoolApr
  });

  return (
    <Box
      sx={{
        backgroundColor: 'gray.16',
        p: '24px',
        width: '100%',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor:
          isSelectable && strategy === item.lpToken.id
            ? 'orange'
            : 'transparent'
      }}
      _hover={{
        filter: 'brightness(120%)'
      }}
      onClick={() => {
        setMode('auto');
        setProvider(item.provider?.toLowerCase() as StrategyProvider);
        setStrategy(item.lpToken.id);
      }}
    >
      <HStack gap="24px">
        <PoolTokensCell
          token0={token0}
          token1={token1}
          isMain
          isNew={badges?.new}
          isVerifiedMeme={badges?.verified_meme}
          isActivePool={isActivePool}
          isNitroPool={Boolean(isNitroPool && nitroDetails)}
        />
        <Flex sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {item.provider === 'Steer' && (
            <Image
              src="/assets/images/providers/steer.svg"
              height={24}
              width={50}
              alt="steer"
            />
          )}
          {item.provider === 'Ichi' && (
            <Image
              src="/assets/images/providers/ichi.svg"
              height={24}
              width={50}
              alt="ichi"
            />
          )}
          {item.provider === 'Gamma' && (
            <Image
              src="/assets/images/providers/gamma.svg"
              height={24}
              width={50}
              alt="gamma"
            />
          )}
          {item.name && (
            <>
              <IoChevronForward color="#707070" />
              <Text fontSize="14px" lineHeight="26px" color="orange">
                {getStrategyAcronim(item.name)}
              </Text>
            </>
          )}
        </Flex>
      </HStack>

      <HStack gap="24px" width="100%" mt={6} alignItems="flex-start">
        <LabeledValue label="TVL" value={formatUSD.format(+tvl)} />
        {/*<LabeledValue label="VOLUME" value="volume???" />*/}
        <LabeledValue
          label="APR"
          value={
            <HStack alignItems="center" gap={1}>
              <Text color="white" fontSize={16} mr={1}>
                {aprValues.apr ? `${formatNumber(+aprValues.apr, 2)}%` : '-'}
              </Text>
              {aprValues.apr && <AprDetailsTooltip data={aprValues} />}
            </HStack>
          }
        />
        {config?.URLS.merklApiUrl || config?.URLS.metromApiUrl ? (
          <LabeledValue
            label="MM"
            value={
              marketMakerCampaign && (
                <MarketMakerCampaignWidget campaignData={marketMaker} />
              )
            }
          />
        ) : null}
        {config?.FLAGS.showNitroPage && isNitroPool && nitroDetails ? (
          <LabeledValue
            label="NITRO"
            value={
              <NitroCellRenderer
                data={nitroDetails}
                totalNitroRewardsUSD={totalNitroRewardsUSD}
                nitroRewards={nitroRewards}
              />
            }
          />
        ) : null}
      </HStack>
    </Box>
  );
};
